import React, { useState } from "react";
import { Menu, X, Sun, Moon, ArrowRight, ChevronDown } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    {
      name: "Services",
      children: [
        { name: "Web Development", path: "/web-development" },
        { name: "Digital Marketing", path: "/digital-marketing" },
        {
          name: "Interior Designing",
          path: "/interior-designing",
        },
      ],
    },
    { name: "Case Study", path: "/case-study" },
    { name: "Blog", path: "/blog" },
    { name: "Internship", path: "/polytechnic-internship" },
    { name: "Contact", path: "/contact" },
  ];

  return (
    <header className="fixed top-0 left-0 w-full z-50">
      <div className="relative overflow-visible border-b border-black/5 dark:border-white/10 bg-white/70 dark:bg-black/50 backdrop-blur-2xl transition-all duration-300">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 left-0 w-[420px] h-[420px] bg-primary/20 blur-[130px] rounded-full" />
          <div className="absolute -top-32 right-0 w-[350px] h-[350px] bg-orange-300/20 blur-[120px] rounded-full" />
        </div>

        <div className="absolute inset-0 pointer-events-none bg-[linear-gradient(rgba(0,0,0,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.03)_1px,transparent_1px)] dark:bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:65px_65px]" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between h-[82px]">
            <a href="/" className="flex items-center cursor-pointer group">
              <div className="relative">
                <div className="absolute inset-0 bg-primary/30 blur-xl rounded-full opacity-0 group-hover:opacity-100 transition duration-500" />
                <img
                  src="/webxsecure-logo.png"
                  alt="WebXSecure"
                  className="relative h-12 w-auto object-contain group-hover:scale-105 transition duration-300"
                />
              </div>

              <div className="leading-tight">
                <h1 className="text-2xl font-black tracking-tight text-gray-900 dark:text-white">
                  Web X Secure
                </h1>
                <span className="hidden md:inline-flex items-center px-1 bg-primary text-white text-xs font-semibold shadow-[0_0_20px_rgba(254,120,83,0.35)]">
                  Next-Gen Secure Innovation
                </span>
              </div>
            </a>

            <nav className="hidden lg:flex items-center gap-2">
              {navLinks.map((item, index) =>
                item.children ? (
                  <div key={index} className="relative group">
                    <button className="flex items-center gap-1 px-4 py-2 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-primary dark:hover:text-primary hover:bg-primary/5 transition-all duration-300">
                      {item.name}
                      <ChevronDown className="w-4 h-4 transition duration-300 group-hover:rotate-180" />
                    </button>

                    <div className="absolute left-0 top-full pt-4 opacity-0 invisible translate-y-3 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300">
                      <div className="w-72 rounded-3xl border border-black/10 dark:border-white/10 bg-white dark:bg-[#0b0b0b] backdrop-blur-xl shadow-2xl p-2">
                        {item.children.map((service, i) => (
                          <a
                            key={i}
                            href={service.path}
                            className="group/item flex items-center justify-between rounded-2xl px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-primary/10 hover:text-primary transition-all duration-300"
                          >
                            {service.name}
                            <ArrowRight className="w-4 h-4 opacity-0 translate-x-1 group-hover/item:opacity-100 group-hover/item:translate-x-0 transition-all" />
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <a
                    key={index}
                    href={item.path}
                    className="px-4 py-2 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-primary dark:hover:text-primary hover:bg-primary/5 transition-all duration-300"
                  >
                    {item.name}
                  </a>
                ),
              )}
            </nav>

            <div className="flex items-center gap-3">
              <button
                onClick={toggleTheme}
                className="relative w-8 h-8 rounded border border-black/10 dark:border-white/10 bg-white/70 dark:bg-white/5 backdrop-blur-xl flex items-center justify-center text-gray-700 dark:text-white hover:scale-105 transition duration-300 overflow-hidden"
              >
                <div className="absolute inset-0 bg-primary/0 hover:bg-primary/10 transition" />
                {theme === "light" ? (
                  <Moon className="relative w-5 h-5" />
                ) : (
                  <Sun className="relative w-5 h-5 text-yellow-400" />
                )}
              </button>

              <a
                href="/contact"
                className="hidden lg:flex group items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white font-semibold hover:scale-105 transition-all duration-300"
              >
                <span>Get Started</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
              </a>

              <button
                onClick={() => setIsOpen(!isOpen)}
                className="lg:hidden relative w-11 h-11 rounded-2xl border border-black/10 dark:border-white/10 bg-white/70 dark:bg-white/5 backdrop-blur-xl flex items-center justify-center text-gray-700 dark:text-white overflow-hidden"
              >
                <div className="absolute inset-0 bg-primary/0 hover:bg-primary/10 transition" />
                {isOpen ? (
                  <X className="relative w-5 h-5" />
                ) : (
                  <Menu className="relative w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        </div>

        <div
          className={`lg:hidden overflow-hidden transition-all duration-500 ${
            isOpen ? "max-h-[650px] opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className="relative border-t border-black/5 dark:border-white/10 bg-white/90 dark:bg-black/90 backdrop-blur-2xl px-6 py-6">
            <div className="flex flex-col gap-2">
              {navLinks.map((item, index) => (
                <div key={index}>
                  {item.children ? (
                    <>
                      <div className="px-4 py-3 flex items-center justify-between font-semibold text-gray-900 dark:text-white">
                        {item.name}
                        <ChevronDown className="w-4 h-4" />
                      </div>

                      <div className="ml-4 flex flex-col gap-1 border-l border-black/10 dark:border-white/10 pl-3">
                        {item.children.map((service, i) => (
                          <a
                            key={i}
                            href={service.path}
                            onClick={() => setIsOpen(false)}
                            className="px-4 py-2 rounded-xl text-sm text-gray-600 dark:text-gray-300 hover:bg-primary/10 hover:text-primary transition"
                          >
                            {service.name}
                          </a>
                        ))}
                      </div>
                    </>
                  ) : (
                    <a
                      href={item.path}
                      onClick={() => setIsOpen(false)}
                      className="block px-4 py-3 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-primary/10 hover:text-primary transition duration-300"
                    >
                      {item.name}
                    </a>
                  )}
                </div>
              ))}
            </div>

            <a
              href="/contact"
              onClick={() => setIsOpen(false)}
              className="mt-5 w-full flex items-center justify-center gap-2 py-3 rounded-2xl bg-primary text-white font-semibold shadow-[0_0_30px_rgba(254,120,83,0.35)]"
            >
              Get Started
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
